import { Box, Text, dark } from '@pancakeswap/uikit'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { KeyboardEvent, ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import styled, { ThemeProvider } from 'styled-components'
import SearchSuggest from 'views/TokenPages/components/SearchSuggest'
import TokenLogo from 'views/TokenPages/components/TokenLogo'
import { inTickCard } from 'views/TokenPages/components/VerifyTicks'
import { tokenHref } from 'views/TokenPages/config'
import { useTokenSuggestions } from 'views/TokenPages/hooks'
import { Icon } from 'views/TokenPages/icons'

/*
 * Token search in the header (Bez, 2026-10-04): the /tokens search (local list first, then BNB Smart Chain through GeckoTerminal or
 * DexScreener, each row marked verified or low liquidity / unverified, and with the token's one tick when it has one) from every page. From 1366 px a field sits in the bar; below
 * that a search button opens the same search over the bar, full width. "/" focuses it anywhere outside a text field.
 * 1366 since 2026-10-07 (was 1260): the CADINU / CBON prices are readable again from 1024 px, and 1260-1365 had no room for both.
 * Room for it (header-fit, 2026-10-03, still no wrap and no overlap 360-1920): the two full price pills now start at 1600 px (one price
 * chip from 968), and the language globe (also in the footer) is left out between 852 and 1259 px.
 */
const FIELD_FROM = 1366
const WIDE = `@media screen and (min-width: ${FIELD_FROM}px)`
const NARROW = `@media screen and (max-width: ${FIELD_FROM - 0.02}px)`

const Root = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  margin-right: 4px;
  ${WIDE} { margin-right: 8px; }
  transition: opacity 160ms ease;
  /* the home hero has its own big search: while it is on screen this one steps aside (its room is kept, so the bar never moves) */
  html[data-hero-search='on'] & {
    opacity: 0;
    visibility: hidden;
    pointer-events: none;
  }
  @media (prefers-reduced-motion: reduce) { transition: none; }
`
const FieldBox = styled.label`
  position: relative;
  display: none;
  ${WIDE} {
    display: flex;
    align-items: center;
    width: 240px;
  }
  @media screen and (min-width: 1600px) { width: 280px; }
  > svg {
    position: absolute;
    left: 12px;
    color: ${({ theme }) => theme.colors.textSubtle};
    pointer-events: none;
  }
`
const Field = styled.input`
  width: 100%;
  height: 40px;
  padding: 0 34px 0 38px;
  border-radius: 12px;
  font-family: inherit;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text};
  background: ${({ theme }) => theme.colors.input};
  border: 1px solid ${({ theme }) => theme.colors.cardBorder};
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.18);
  caret-color: ${({ theme }) => theme.colors.primary};
  transition: border-color 120ms ease, box-shadow 120ms ease;
  &::placeholder { color: ${({ theme }) => theme.colors.textSubtle}; opacity: 1; }
  &:focus { outline: none; border-color: ${({ theme }) => theme.colors.primary}; box-shadow: 0 0 0 3px rgba(127, 212, 232, 0.2); }
  &::-webkit-search-cancel-button { display: none; }
  @media (prefers-reduced-motion: reduce) { transition: none; }
`
const Kbd = styled.kbd`
  position: absolute;
  right: 10px;
  min-width: 20px;
  height: 20px;
  padding: 0 5px;
  border-radius: 6px;
  font-family: inherit;
  font-size: 12px;
  line-height: 18px;
  text-align: center;
  color: ${({ theme }) => theme.colors.textSubtle};
  border: 1px solid ${({ theme }) => theme.colors.cardBorder};
  pointer-events: none;
`
const Clear = styled.button`
  position: absolute;
  right: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: ${({ theme }) => theme.colors.textSubtle};
  cursor: pointer;
  &:hover { color: ${({ theme }) => theme.colors.text}; }
  &:focus-visible { outline: 2px solid ${({ theme }) => theme.colors.primary}; }
`
const OpenBtn = styled.button`
  display: none;
  ${NARROW} {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border: 0;
    border-radius: 12px;
    background: transparent;
    color: ${({ theme }) => theme.colors.textSubtle};
    cursor: pointer;
  }
  &:hover { color: ${({ theme }) => theme.colors.text}; background: ${({ theme }) => theme.colors.tertiary}; }
  &:focus-visible { outline: 2px solid ${({ theme }) => theme.colors.primary}; outline-offset: 1px; }
`
/* the desktop drop-down: right-aligned under the field, wider than it */
const Drop = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  z-index: 5;
  width: min(440px, calc(100vw - 32px));
  white-space: normal;
`
export const Panel = styled.div`
  max-height: min(70vh, 560px);
  overflow-y: auto;
  overscroll-behavior: contain;
  border-radius: 18px;
  box-shadow: 0 18px 44px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(127, 212, 232, 0.08);
  color: ${({ theme }) => theme.colors.text};
  background: rgba(10, 14, 34, 0.98);
  /* the /tokens list keeps its own rows; here it fills the panel */
  ul[data-testid='tp-suggest'] { margin: 0; max-width: none; border: 0; background: transparent; }
`
const Note = styled.div`
  padding: 14px 16px;
`
const OpenRow = styled.a`
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 56px;
  margin: 6px;
  padding: 6px 10px;
  border-radius: 12px;
  color: ${({ theme }) => theme.colors.text};
  text-decoration: none;
  &:hover, &:focus-visible { background: rgba(127, 212, 232, 0.1); outline: none; }
`
/* below 1366 px: the search takes the bar, full width, with the page dimmed under its results */
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 40;
  display: flex;
  flex-direction: column;
  background: rgba(3, 5, 14, 0.72);
  backdrop-filter: blur(2px);
`
const Bar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  height: 56px;
  padding: 0 8px 0 12px;
  background: #0b0f24;
  border-bottom: 1px solid rgba(127, 212, 232, 0.18);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
  > div { position: relative; flex: 1; display: flex; align-items: center; min-width: 0; }
  > div > svg { position: absolute; left: 12px; color: #8f9bb8; pointer-events: none; }
`
const OverlayField = styled(Field)`
  height: 44px;
  font-size: 16px; /* 16 px: iOS does not zoom into the field */
  padding-right: 12px;
`
const Close = styled.button`
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border: 0;
  border-radius: 12px;
  background: transparent;
  color: #dfe9ff;
  cursor: pointer;
  &:focus-visible { outline: 2px solid #7fd4e8; }
`
const Below = styled.div`
  padding: 8px 8px 0;
  min-height: 0;
  flex: 1;
  > div { max-height: calc(100dvh - 56px - 24px); }
`

const isTyping = (t: EventTarget | null) => {
  const el = t as HTMLElement | null
  return Boolean(el && (el.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName)))
}

/** The results: the /tokens suggestion list, or what to do when there is none. */
export function Results({ q, s }: { q: string; s: ReturnType<typeof useTokenSuggestions> }) {
  const text = q.trim()
  if (!text) {
    return (
      <Note>
        <Text fontSize="14px" color="textSubtle">Search any BNB Smart Chain token by name, symbol or 0x contract address.</Text>
      </Note>
    )
  }
  const busy = s.failed || Boolean(s.result?.busy)
  const empty = s.done && !s.rows.length && !busy && !s.result?.notToken
  return (
    <>
      <SearchSuggest rows={s.rows} result={s.result} loading={s.loading} failed={s.failed} isAddress={s.isAddress} />
      {s.isAddress && busy && (
        <Link href={tokenHref(s.address)} passHref legacyBehavior>
          <OpenRow data-testid="hdr-search-open-address">
            <TokenLogo address={s.address} symbol="?" size={36} />
            <Box style={{ minWidth: 0 }}>
              <Text bold>Open this token</Text>
              <Text fontSize="12px" color="textSubtle" style={{ overflowWrap: 'anywhere' }}>{s.address}</Text>
            </Box>
          </OpenRow>
        </Link>
      )}
      {s.invalid && !s.rows.length && (
        <Note><Text fontSize="14px" color="textSubtle">Type two letters or more, or paste a full 0x address.</Text></Note>
      )}
      {empty && !s.invalid && (
        <Note data-testid="hdr-search-empty">
          <Text bold fontSize="14px">No token matches “{text}”</Text>
          <Text fontSize="13px" color="textSubtle" mt="2px">Paste a contract address to open any BNB Smart Chain token with a pool on a supported exchange.</Text>
        </Note>
      )}
    </>
  )
}

/** The panel is a night island like the token pages, whatever the site theme is. */
export const Night = ({ children }: { children: ReactNode }) => (
  <ThemeProvider theme={dark}>
    <div data-theme="dark">{children}</div>
  </ThemeProvider>
)

export default function HeaderSearch() {
  const router = useRouter()
  const [q, setQ] = useState('')
  const [dropOpen, setDropOpen] = useState(false)
  const [overlay, setOverlay] = useState(false)
  const s = useTokenSuggestions(q)
  const root = useRef<HTMLDivElement>(null)
  const field = useRef<HTMLInputElement>(null)
  const overlayField = useRef<HTMLInputElement>(null)
  const openBtn = useRef<HTMLButtonElement>(null)
  const panel = useRef<HTMLDivElement>(null)

  const closeAll = useCallback(() => {
    setDropOpen(false)
    setOverlay(false)
  }, [])
  // a picked token (or any navigation) closes the search and clears it
  useEffect(() => {
    const done = () => {
      closeAll()
      setQ('')
      field.current?.blur()
    }
    router.events.on('routeChangeStart', done)
    return () => router.events.off('routeChangeStart', done)
  }, [router.events, closeAll])
  // "/" anywhere outside a text field
  useEffect(() => {
    const key = (e: globalThis.KeyboardEvent) => {
      if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey || isTyping(e.target)) return
      if (document.documentElement.dataset.heroSearch === 'on') return // the home hero's search takes "/" while it is on screen
      e.preventDefault()
      if (window.matchMedia(`(min-width: ${FIELD_FROM}px)`).matches) field.current?.focus()
      else setOverlay(true)
    }
    window.addEventListener('keydown', key)
    return () => window.removeEventListener('keydown', key)
  }, [])
  // the desktop drop-down closes on a click elsewhere
  useEffect(() => {
    if (!dropOpen) return undefined
    const down = (e: PointerEvent) => {
      // a row's tick card is portalled to <body>: a click inside it is not "elsewhere"
      if (!root.current?.contains(e.target as Node) && !inTickCard(e.target)) setDropOpen(false)
    }
    document.addEventListener('pointerdown', down)
    return () => document.removeEventListener('pointerdown', down)
  }, [dropOpen])
  // the overlay: focus its field, keep the page still under it, and fold it away if the window grows past FIELD_FROM
  useEffect(() => {
    if (!overlay) return undefined
    const t = setTimeout(() => overlayField.current?.focus(), 0)
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const mq = window.matchMedia(`(min-width: ${FIELD_FROM}px)`)
    const grow = () => mq.matches && setOverlay(false)
    mq.addEventListener?.('change', grow)
    return () => {
      clearTimeout(t)
      document.body.style.overflow = prev
      mq.removeEventListener?.('change', grow)
    }
  }, [overlay])

  const go = () => {
    if (s.isAddress) router.push(tokenHref(s.address))
    else if (s.rows[0]) router.push(tokenHref(s.rows[0].address))
  }
  const links = () => Array.from(panel.current?.querySelectorAll<HTMLAnchorElement>('a[href]') ?? [])
  const fieldKeys = (e: KeyboardEvent<HTMLInputElement>, onEscape: () => void) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      go()
    } else if (e.key === 'ArrowDown') {
      const first = links()[0]
      if (first) {
        e.preventDefault()
        first.focus()
      }
    } else if (e.key === 'Escape') {
      e.preventDefault()
      onEscape()
    }
  }
  const panelKeys = (e: KeyboardEvent<HTMLDivElement>, input: HTMLInputElement | null, onEscape: () => void) => {
    const all = links()
    const i = all.indexOf(document.activeElement as HTMLAnchorElement)
    if (e.key === 'ArrowDown' && i >= 0) {
      e.preventDefault()
      all[Math.min(i + 1, all.length - 1)]?.focus()
    } else if (e.key === 'ArrowUp' && i >= 0) {
      e.preventDefault()
      if (i === 0) input?.focus()
      else all[i - 1]?.focus()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      onEscape()
    }
  }
  const closeOverlay = () => {
    setOverlay(false)
    openBtn.current?.focus()
  }
  const showDrop = dropOpen && Boolean(q.trim())

  return (
    <Root ref={root} data-testid="hdr-search">
      <FieldBox>
        <Icon name="search" size={18} />
        <Field
          ref={field}
          type="search"
          value={q}
          onChange={(e) => {
            setQ(e.target.value)
            setDropOpen(true)
          }}
          onFocus={() => setDropOpen(true)}
          onKeyDown={(e) =>
            fieldKeys(e, () => {
              if (q) setQ('')
              else field.current?.blur()
              setDropOpen(false)
            })
          }
          placeholder="Search tokens"
          aria-label="Search tokens by name, symbol or address"
          aria-expanded={showDrop}
          aria-controls={showDrop ? 'hdr-search-panel' : undefined}
          autoComplete="off"
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
          enterKeyHint="search"
          data-testid="hdr-search-field"
        />
        {q ? (
          <Clear type="button" aria-label="Clear the search" onClick={() => { setQ(''); field.current?.focus() }}><Icon name="close" size={16} /></Clear>
        ) : (
          <Kbd aria-hidden="true">/</Kbd>
        )}
      </FieldBox>
      {showDrop && (
        <Drop>
          <Night>
            <Panel id="hdr-search-panel" ref={panel} data-testid="hdr-search-panel" onKeyDown={(e) => panelKeys(e, field.current, () => { setDropOpen(false); field.current?.focus() })}>
              <Results q={q} s={s} />
            </Panel>
          </Night>
        </Drop>
      )}

      <OpenBtn ref={openBtn} type="button" aria-label="Search tokens" aria-haspopup="dialog" aria-expanded={overlay} onClick={() => setOverlay(true)} data-testid="hdr-search-open">
        <Icon name="search" size={22} />
      </OpenBtn>
      {overlay &&
        typeof document !== 'undefined' &&
        createPortal(
          <Night>
            <Overlay
              role="dialog"
              aria-modal="true"
              aria-label="Search tokens"
              data-testid="hdr-search-overlay"
              onPointerDown={(e) => {
                if (e.target === e.currentTarget) closeOverlay()
              }}
            >
              <Bar>
                <div>
                  <Icon name="search" size={18} />
                  <OverlayField
                    ref={overlayField}
                    type="search"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    onKeyDown={(e) => fieldKeys(e, closeOverlay)}
                    placeholder="Name, symbol or 0x address"
                    aria-label="Search tokens by name, symbol or address"
                    autoComplete="off"
                    autoCapitalize="off"
                    autoCorrect="off"
                    spellCheck={false}
                    enterKeyHint="search"
                    data-testid="hdr-search-input"
                  />
                </div>
                <Close type="button" aria-label="Close the search" onClick={closeOverlay} data-testid="hdr-search-close"><Icon name="close" size={22} /></Close>
              </Bar>
              <Below
                onPointerDown={(e) => {
                  if (e.target === e.currentTarget) closeOverlay()
                }}
              >
                <Panel ref={panel} data-testid="hdr-search-panel" onKeyDown={(e) => panelKeys(e, overlayField.current, closeOverlay)}>
                  <Results q={q} s={s} />
                </Panel>
              </Below>
            </Overlay>
          </Night>,
          document.body,
        )}
    </Root>
  )
}
